// Slide JSON 前端镜像类型与工具（M3）
// ⚠️ 类型/schema 与后端 supabase/functions/_shared/pptx/types.ts 保持同步；
//    设计 tokens（THEME/METRIC_COLORS）唯一来源为 supabase/functions/_shared/pptx/tokens.ts，
//    前端经 scripts/sync-tokens.mjs 生成 ./tokens.generated.ts，禁止手工镜像（G8）。

import { DEFAULT_THEME_ID, FONT, getTheme, hex } from "./tokens.generated";
import type { SwissTheme } from "./tokens.generated";

export type Layout =
  | "cover" | "concept" | "insight" | "data" | "strategy"
  | "channel" | "roadmap" | "financial" | "risk" | "vision";

export type Block =
  | { type: "metric"; label: string; value: string }
  | { type: "bullets"; items: string[] }
  | { type: "text"; text: string }
  | { type: "image"; assetId?: string; url?: string; path?: string; prompt?: string }
  // ---- V2 block（与后端 types.ts 同步）----
  | { type: "quote"; text: string; cite?: string }
  | { type: "compare"; left: { title: string; items: string[] }; right: { title: string; items: string[] } }
  | { type: "timeline"; items: { title: string; desc?: string }[] }
  | { type: "ledger"; items: { name: string; value: string; note?: string }[] }
  | { type: "chart"; items: { label: string; value: number; unit?: string }[] }
  | { type: "cards"; items: { title: string; desc?: string; bullets?: string[] }[] }
  // ---- KBS block（与后端 types.ts 同步，032 §4.1）----
  | {
      type: "scatter";
      xNeg: string; xPos: string; yNeg: string; yPos: string;
      points: { name: string; x: number; y: number; role: "competitor" | "self" }[];
      gap: { x: number; y: number; label: string };
    }
  | {
      type: "gridmatrix";
      xAxis: string; yAxis: string; rows: number; cols: number;
      cells: { row: number; col: number; title: string; desc: string; stars: number }[];
    }
  | { type: "pyramid"; layers: { title: string; desc: string }[] }
  | {
      type: "table";
      colHeads: string[];
      rowHeads: { label: string; note?: string }[];
      cells: string[][];
      marks?: { row: number; col: number; tag: string }[];
    }
  // ---- 主题库扩展 v1.1 数据组件（与后端 types.ts 同步，043 §4.10）----
  | { type: "waterfall"; unit?: string; items: { label: string; delta: number | null }[] }
  | {
      type: "gantt";
      ticks: string[];
      lanes?: string[];
      tasks: { label: string; start: number; end: number; lane?: number }[];
    }
  | { type: "funnel"; unit?: string; tiers: { label: string; value: number }[] }
  | {
      type: "dumbbell";
      items: { name: string; before: number; after: number; lo: number; hi: number; unit?: string }[];
      hero?: number;
    }
  | { type: "bullet"; unit?: string; items: { label: string; actual: number; target: number; max?: number }[] };

export interface SlideContent {
  layout: Layout;
  title: string;
  subtitle?: string;
  blocks: Block[];
  notes?: string;
  /** V2 版式 ID 与底色（019_Spec；缺省按 LEGACY_LAYOUT_MAP 推导） */
  layoutV2?: string;
  bg?: "paper" | "ink" | "accent";
}

/** 编辑器内使用的页模型（id 为服务端行 id，本地新增页用临时 id） */
export interface EditorSlide {
  id?: string;
  layout: Layout;
  content: SlideContent;
}

export const LAYOUT_LABEL: Record<Layout, string> = {
  cover: "封面", concept: "核心概念", insight: "洞察", data: "数据",
  strategy: "策略", channel: "渠道", roadmap: "路线图",
  financial: "经营测算", risk: "风险", vision: "愿景",
};

export const LAYOUT_OPTIONS = Object.keys(LAYOUT_LABEL) as Layout[];

// ---- V2 版式元信息（020 PRD §5.2/§5.3：进度页版式叙事 + 编辑器版式名展示）----

/** V2 22 版式中文名（与后端 v2/registry.ts 的 RENDERERS 顺序一致；KBS 032 §4 新增 4 版式 + 043 §4.10 新增 5 版式） */
export const V2_LAYOUT_NAMES: Record<string, string> = {
  cover: "封面", divider: "章节页", statement: "金句页", "kpi-hero": "巨数页",
  "bar-chart": "条形图页", "duo-compare": "双栏对比页", "timeline-h": "横向时间线",
  "timeline-v": "纵向时间线", cards: "卡片组页", matrix: "矩阵网格页",
  ledger: "账单页", "image-text": "图文页", closing: "封底",
  "perceptual-map": "感知图", "quad-matrix": "网格矩阵", pyramid: "金字塔", "grid-table": "网格表",
  waterfall: "瀑布桥", gantt: "排期泳道", funnel: "转化漏斗", dumbbell: "对比记分板", bullet: "KPI 目标带",
};

/** 底色中文名（outline 节奏表的 bg 字段） */
export const V2_BG_LABEL: Record<string, string> = { paper: "浅底", ink: "深底", accent: "强调色底" };

/** 旧 layout → V2 版式兜底推导（镜像后端 tokens.ts LEGACY_LAYOUT_MAP） */
const LEGACY_TO_V2: Record<string, string> = {
  cover: "cover", concept: "statement", vision: "closing", data: "kpi-hero",
  insight: "duo-compare", strategy: "cards", channel: "matrix",
  roadmap: "timeline-h", financial: "ledger", risk: "cards",
};

/** 解析 slide 实际使用的 V2 版式（与后端 resolveLayoutV2 同规则：显式值 → 旧枚举映射 → cards） */
export function resolveLayoutV2(c: Pick<SlideContent, "layout" | "layoutV2">): string {
  const v = c.layoutV2;
  if (v && V2_LAYOUT_NAMES[v]) return v;
  return LEGACY_TO_V2[c.layout] ?? "cards";
}

/**
 * PPT 主题色（消费方契约，带 # 前缀）。
 * 过渡映射（Phase 0 T0.3）：色值全部来自 tokens.generated.ts（swiss-ikb 主题），
 * 旧字段语义就近对应；Phase 3 重写 SlideCanvas 等消费方后本对象将按 V2 主题结构重构。
 */
const __swiss = getTheme(DEFAULT_THEME_ID) as SwissTheme;

/** CSS 字体栈：含空格的字体名补引号 */
const fontStack = (stack: readonly string[]): string =>
  stack.map((f) => (f.includes(" ") ? `'${f}'` : f)).join(", ");

export const THEME = {
  primary: hex(__swiss.accent),       // 旧主蓝 → 主题 accent（IKB）
  primaryDark: hex(__swiss.grey.ink), // 深底/标题深色 → ink
  primaryLight: hex(__swiss.grey.grey1),
  accent: hex(__swiss.accent),        // 新体系仅单一 accent，旧次强调色就近并入
  ok: hex(__swiss.grey.grey3),        // 状态色无对应，过渡期内映射到中性灰/accent
  warn: hex(__swiss.accent),
  ink: hex(__swiss.grey.ink),
  muted: hex(__swiss.grey.grey3),
  paper: hex(__swiss.grey.paper),
  soft: hex(__swiss.grey.grey1),
  line: hex(__swiss.grey.grey2),
  gold: hex(__swiss.accent),
  coverSub: hex(__swiss.accentOn),    // accent 底上的副标题文字色
  font: fontStack(FONT.swiss.body.stack),
};

/** 指标卡顶部色条轮转色（与 layouts.ts metricCards 一致） */
export const METRIC_COLORS = [THEME.primary, THEME.accent, THEME.ok, THEME.warn, THEME.gold];

/**
 * 不可变按路径写入：path 形如 "title" / "blocks.2.items.0"。
 * 返回新对象（不改原引用），配合 React state 与撤销栈。
 */
export function setByPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.split(".");
  const clone = (Array.isArray(obj) ? [...obj] : { ...(obj as Record<string, unknown>) }) as T;
  let cur = clone as Record<string, unknown>;
  let src = obj as Record<string, unknown>;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const nextSrc = src?.[k];
    const nextClone = Array.isArray(nextSrc) ? [...nextSrc] : { ...(nextSrc as Record<string, unknown> ?? {}) };
    cur[k] = nextClone;
    cur = nextClone as Record<string, unknown>;
    src = nextSrc as Record<string, unknown>;
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

/** 新增页工厂（「新增页」按钮用，对齐原型默认结构） */
export function newSlide(layout: Layout = "insight"): EditorSlide {
  return {
    layout,
    content: {
      layout,
      title: "点击编辑标题",
      subtitle: "点击编辑副标题",
      blocks: [
        { type: "bullets", items: ["要点一", "要点二", "要点三"] },
        { type: "image", prompt: "配图位（点击替换）" },
      ],
    },
  };
}
